import { useEffect, useMemo, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangleIcon, InfoIcon, RefreshCwIcon } from "lucide-react";
import { toast } from "sonner";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { AnalyticsFilterBar } from "@/components/analytics/AnalyticsFilterBar";
import { useAnalyticsParams } from "@/components/analytics/useAnalyticsParams";
import {
  BreakdownBars,
  USER_ACTIVITY_VIZ_CLASS,
  USER_ACTIVITY_VIZ_STYLES,
  topWithOther,
} from "@/components/user-activity/BreakdownBars";
import {
  APP_LABELS,
  BROWSER_LABELS,
  DEVICE_TYPE_LABELS,
  METHOD_LABELS,
  OS_LABELS,
  countryLabel,
} from "@/components/user-activity/labels";
import { UserActivityUsersTable } from "@/components/user-activity/UserActivityUsersTable";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatNumber } from "@/lib/format";
import { num } from "@/lib/matching";
import { chartTooltipProps } from "@/components/charts/ChartTooltip";

type Summary = components["schemas"]["UserActivitySummary"];

const MAX_COUNTRIES = 15;

const ACTIVE_HINT =
  "Someone counts as active on a day if they signed in or had the app open that day (an open tab refreshes its session about every 10 minutes). Today, 7 and 30 days end on the last day of the selected range.";

/** "2026-09-08" -> "8 Sep", parsed by hand so a date-only string never shifts with the local timezone. */
function dateLabel(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return dateStr;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

export function UserActivityPage() {
  const { clientIds, from, to } = useAnalyticsParams();
  const clientIdsKey = clientIds.join(",");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/user-activity/summary", {
        params: { query: { From: from, To: to, ClientIds: clientIds.length > 0 ? clientIds : undefined } },
      });
      if (cancelled) return;
      if (apiError || !data) {
        setError("User activity could not be loaded.");
        toast.error("User activity could not be loaded.");
      } else {
        setSummary(data);
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
    // clientIdsKey stands in for clientIds (same input, stable key).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdsKey, from, to, refreshKey]);

  return (
    <div className={USER_ACTIVITY_VIZ_CLASS}>
      <style>{USER_ACTIVITY_VIZ_STYLES}</style>
      <AnalyticsFilterBar showOrganisation={false} />
      <div className="mx-auto w-full max-w-[100rem] space-y-6 p-8">
        <div>
          <h1 className="text-2xl font-bold">User activity</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Who is signing in and using Dolfin, across {clientIds.length > 0 ? "the selected clients" : "every client"}.
          </p>
        </div>

        {loading && !summary ? (
          <LoadingState />
        ) : error && !summary ? (
          <ErrorState message={error} onRetry={() => setRefreshKey((k) => k + 1)} />
        ) : summary ? (
          <SummarySections summary={summary} />
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>
              Everyone under the selected clients, including people who have never signed in. Sign-ins count the selected
              range; last active and last sign-in are all-time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <UserActivityUsersTable clientIds={clientIds} from={from} to={to} />
          </CardContent>
        </Card>

        <p className="text-muted-foreground text-xs">
          Location is approximate: city-level, worked out from the IP address, which is never stored. Sign-ins through an
          integrator's own app usually have no location, and neither do sign-ins made straight to the API.
        </p>
      </div>
    </div>
  );
}

function SummarySections({ summary }: { summary: Summary }) {
  const signIns = num(summary.signIns);
  const daily = useMemo(
    () =>
      (summary.daily ?? []).map((d) => ({
        label: dateLabel(d.date),
        signIns: num(d.signIns),
        activeUsers: num(d.activeUsers),
      })),
    [summary.daily],
  );

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Tile label="Active today" value={num(summary.activeToday)} hint={ACTIVE_HINT} />
        <Tile label="Active last 7 days" value={num(summary.active7Days)} hint={ACTIVE_HINT} />
        <Tile label="Active last 30 days" value={num(summary.active30Days)} hint={ACTIVE_HINT} />
        <Tile label="Sign-ins in range" value={signIns} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-1.5">
            Sign-ins and active users
            <InfoHint label="Sign-ins and active users">{ACTIVE_HINT}</InfoHint>
          </CardTitle>
          <CardDescription>Each day in the selected range, in UTC.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={daily} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border, #e5e7eb)" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  stroke="var(--color-muted-foreground, #6b7280)"
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground, #6b7280)" allowDecimals={false} />
                <Tooltip {...chartTooltipProps} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="signIns" name="Sign-ins" fill="var(--series-1)" />
                <Line
                  dataKey="activeUsers"
                  name="Active users"
                  type="monotone"
                  stroke="var(--series-2)"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {signIns === 0 ? (
        <div className="border-border flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
          <InfoIcon className="text-muted-foreground size-6" />
          <p className="text-sm font-medium">No sign-ins in this window</p>
          <p className="text-muted-foreground max-w-md text-sm">
            Widen the date range or choose other clients. People who stayed signed in still count as active above.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Sign-ins by country</CardTitle>
              <CardDescription>Top {MAX_COUNTRIES}, the rest grouped as Other.</CardDescription>
            </CardHeader>
            <CardContent>
              <BreakdownBars
                title="Country"
                items={topWithOther(
                  (summary.byCountry ?? []).map((c) => ({ label: countryLabel(c.country), value: num(c.signIns) })),
                  MAX_COUNTRIES,
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Devices</CardTitle>
              <CardDescription>What people signed in from.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <BreakdownBars
                title="Device type"
                items={(summary.byDeviceType ?? []).map((d) => ({ label: DEVICE_TYPE_LABELS[d.deviceType], value: num(d.signIns) }))}
              />
              <BreakdownBars
                title="Operating system"
                items={(summary.byOs ?? []).map((d) => ({ label: OS_LABELS[d.os], value: num(d.signIns) }))}
              />
              <BreakdownBars
                title="Browser"
                items={(summary.byBrowser ?? []).map((d) => ({ label: BROWSER_LABELS[d.browser], value: num(d.signIns) }))}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>How people signed in</CardTitle>
              <CardDescription>Sign-in method, and the app they came through.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <BreakdownBars
                title="Method"
                items={(summary.byMethod ?? []).map((m) => ({ label: METHOD_LABELS[m.method], value: num(m.signIns) }))}
              />
              <BreakdownBars
                title="App"
                items={(summary.byApp ?? []).map((a) => ({ label: APP_LABELS[a.app], value: num(a.signIns) }))}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Active organisations by client</CardTitle>
              <CardDescription>Organisations with at least one active member in the selected range.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Active organisations</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(summary.activeOrganisationsByClient ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-muted-foreground h-16 text-center">
                          No active organisations.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (summary.activeOrganisationsByClient ?? []).map((c) => (
                        <TableRow key={c.clientId}>
                          <TableCell>{c.clientName}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatNumber(num(c.activeOrganisations))}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

function Tile({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <Card className="gap-1 py-4">
      <CardContent className="px-4">
        <p className="text-muted-foreground flex items-center gap-1 text-sm">
          {label}
          {hint && <InfoHint label={label}>{hint}</InfoHint>}
        </p>
        <p className="mt-1 text-3xl font-semibold tabular-nums">{formatNumber(value)}</p>
      </CardContent>
    </Card>
  );
}

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="bg-muted h-24 animate-pulse rounded-xl" />
        ))}
      </div>
      <div className="bg-muted h-80 animate-pulse rounded-xl" />
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="border-destructive/30 bg-destructive/5 flex flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center">
      <AlertTriangleIcon className="text-destructive size-6" />
      <p className="text-sm font-medium">Could not load user activity</p>
      <p className="text-muted-foreground max-w-md text-sm">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="border-border hover:bg-accent mt-1 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition"
      >
        <RefreshCwIcon className="size-3.5" />
        Try again
      </button>
    </div>
  );
}
